import React, { useState } from 'react';
import { Animated, Text, View, StyleSheet } from 'react-native';
import i18n from 'i18n-js';
import OutsidePressHandler from 'react-native-outside-press';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Element, scale, notchTop } from '@components/frame';
import TouchableElement from '@components/common/touchableElement';
import BackButton from '@components/common/backButton';
import { isPhone, isWeb, navigateTo } from '@helpers';

import { font } from '@styles/fonts';
import Button from '@styles/buttons';
import Selector from '@styles/selectors';
import { getBoxShadowStyle } from '@styles/shadow';
import FeatureFlags from '@components/featureFlags';
import { SCREEN_PADDING_HORIZONTAL } from '@v2/pages/common/constants/base.constants';
import { isLimitedSettingsStack } from '@components/settings/v2/common/navigationParams';

const ActionModal = ({ C, modalItems, setShowModal }) => {
  const submenuBg = isWeb ? 'Background/Light/Field on submenu' : 'Background/Light/Field on Panel';

  return (
    <View
      style={[
        getBoxShadowStyle({ type: 'Field', C }),
        {
          backgroundColor: C(submenuBg),
          borderRadius: scale(12),
          minWidth: scale(134),
          position: 'absolute',
          right: 0,
          top: scale(34),
        },
      ]}
    >
      {modalItems.map((item, index) => {
        const firstItem = index == 0;
        const lastItem = index == modalItems.length - 1;
        return (
          <TouchableElement
            onPress={() => {
              setShowModal(false);
              item.action();
            }}
            hitSlop={{ top: 0, bottom: 0, left: 0, right: 0 }}
          >
            {({ boxStyle, pressHoverColor }) => (
              <View
                key={item.name}
                style={[
                  boxStyle,
                  {
                    backgroundColor: C(pressHoverColor(submenuBg)),
                    paddingHorizontal: scale(20),
                    height: scale(41),
                    justifyContent: 'center',
                    borderBottomWidth: StyleSheet.hairlineWidth,
                    borderBottomColor: C('Hairline/Light/No trans'),
                    zIndex: 100000,
                  },
                  firstItem && {
                    borderTopLeftRadius: scale(12),
                    borderTopRightRadius: scale(12),
                  },
                  lastItem && {
                    borderBottomLeftRadius: scale(12),
                    borderBottomRightRadius: scale(12),
                    borderBottomWidth: 0,
                  },
                ]}
              >
                <Text style={[font.Body, { color: C('Label/Light/Primary') }]}>{i18n.t(item.name)}</Text>
              </View>
            )}
          </TouchableElement>
        );
      })}
    </View>
  );
};
export default ({ C, tabs, selectedTab, toggleInviteUser, toggleAddTool, toggleAddMachine, rootStack }) => {
  const [showModal, setShowModal] = useState(false);
  const isLimited = isLimitedSettingsStack(rootStack);
  const insets = useSafeAreaInsets();
  const limitedTopOffset = isLimited ? 20 : 0;

  const textLength = i18n.t('Settings').length;
  const needOffset = textLength > 25;

  const modalItems = [
    {
      name: 'Add User',
      action: toggleInviteUser,
    },
    {
      name: 'Add Device',
      action: () => navigateTo('AddDevice'),
    },
    {
      name: 'Add Machine',
      action: toggleAddMachine,
    },
  ];

  return (
    <Animated.View
      style={{
        height: scale(isPhone ? (needOffset ? 149 : 109) + notchTop + limitedTopOffset : 179),
        width: '100%',
        zIndex: 1000000,
      }}
    >
      {isLimited && (
        <Element
          {...{
            configMobile: {
              position: {
                top: 12 + insets.top,
                left: SCREEN_PADDING_HORIZONTAL,
              },
              height: 22,
            },
          }}
        >
          <BackButton />
        </Element>
      )}
      <Element
        {...{
          config: {
            position: {
              top: 67,
              left: SCREEN_PADDING_HORIZONTAL,
            },
            height: 39,
          },
          configMobile: {
            position: {
              top: 45 + notchTop + limitedTopOffset,
              left: isPhone ? 21 : SCREEN_PADDING_HORIZONTAL,
            },
          },
        }}
      >
        <Text
          style={[
            {
              color: C('Label / Light / Primary'),
              fontWeight: '700',
            },
            font['Large Title'],
          ]}
        >
          {i18n.t('Settings')}
        </Text>
      </Element>
      {!isLimited && (
        <Element
          {...{
            config: {
              position: {
                top: 74,
                right: SCREEN_PADDING_HORIZONTAL,
              },
              // width: 125,
              height: 36,
            },
            configMobile: {
              position: {
                top: 48 + notchTop,
                right: SCREEN_PADDING_HORIZONTAL,
              },
            },
          }}
        >
          <View>
            <OutsidePressHandler
              onOutsidePress={() => {
                setShowModal(false);
              }}
            >
              <View style={{ opacity: showModal ? 0.3 : 1 }}>
                <Button
                  {...{
                    variant: 'M',
                    type: 'Primary',
                    onPress: () => setShowModal(!showModal),
                  }}
                >
                  <Text
                    style={[
                      {
                        color: C('Label/Light/Selected'),
                        textTransform: 'uppercase',
                      },
                      font['Button 2'],
                    ]}
                  >
                    {i18n.t('ADD')}
                  </Text>
                </Button>
              </View>
              {showModal && <ActionModal {...{ C, modalItems, setShowModal }} />}
            </OutsidePressHandler>
          </View>
        </Element>
      )}
      <Element
        {...{
          config: {
            position: {
              top: 137,
            },
            horizontalCenter: 0,
            width: 816,
            height: 39,
            flexDirection: 'row',
            display: 'flex',
            justifyContent: 'center',
            opacity: 1,
          },
          configMobile: {
            display: 'none',
            height: 0,
            opacity: 0,
          },
        }}
      >
        {tabs.map((item) => (
          <FeatureFlags {...{ name: item.flag, C }} key={item.value}>
            <Selector
              onPress={() => {
                navigateTo('Settings', { screen: item.value });
              }}
              {...{ selected: item.value === selectedTab, variant: 'L', text: i18n.t(item.value) }}
            />
          </FeatureFlags>
        ))}
      </Element>
    </Animated.View>
  );
};
